import { InputModel, isMovementKey, type PointerKind, type PointerSample, type SafeArea } from './inputModel';
import type { Settings } from './settings';
import { AIM_REACH, AIM_START, LOCKED_PAD_LANDSCAPE, LOCKED_PAD_PORTRAIT, PAD_DEAD_ZONE, PAD_PORTRAIT_SCALE, PAD_RADIUS, QUICK_TAP_MS, TURN_COOLDOWN_MS } from './uiConstants';

/** The browser's safe-area insets, published as CSS variables in style.css. */
function safeArea(): SafeArea {
  const css = getComputedStyle(document.documentElement);
  const px = (name: string): number => {
    const v = parseFloat(css.getPropertyValue(name));
    return Number.isFinite(v) ? v : 0;
  };
  return { left: px('--safe-left'), right: px('--safe-right'), bottom: px('--safe-bottom') };
}

/** Takes the pointer stream while the board is on screen; the pads are off then (docs/STEUERUNG.md, Abschnitt 7). */
export interface BoardPointerSample extends PointerSample {
  button?: number;
  buttons?: number;
  isPrimary?: boolean;
}

export interface BoardPointerTarget {
  pointerDown(s: BoardPointerSample): void;
  pointerMove(s: BoardPointerSample): void;
  pointerUp(s: BoardPointerSample): void;
  pointerCancel(id: number): void;
  cancelGesture(): void;
  clearDrawings(): void;
}

/**
 * Wires Pointer Events and the keyboard on `canvas` to an InputModel. Returns the model, a detach and
 * `setBoard`, which hands the pointers to the board (or back to the pads).
 */
export function attachInput(canvas: HTMLCanvasElement, settings: Settings): { model: InputModel; detach: () => void; setBoard: (target: BoardPointerTarget | null) => void } {
  const model = new InputModel(
    {
      padRadius: PAD_RADIUS,
      portraitScale: PAD_PORTRAIT_SCALE,
      deadZone: PAD_DEAD_ZONE,
      aimStart: AIM_START,
      quickTapMs: QUICK_TAP_MS,
      lockedLandscape: LOCKED_PAD_LANDSCAPE,
      lockedPortrait: LOCKED_PAD_PORTRAIT,
      turnCooldownMs: TURN_COOLDOWN_MS,
      aimReach: AIM_REACH,
    },
    settings,
  );
  let board: BoardPointerTarget | null = null;

  // an unknown pointerType is treated as a finger: dragging and tapping keep working either way
  const kindOf = (e: PointerEvent): PointerKind => (e.pointerType === 'mouse' || e.pointerType === 'pen' ? e.pointerType : 'touch');
  const sample = (e: PointerEvent): BoardPointerSample => {
    const rect = canvas.getBoundingClientRect();
    return { id: e.pointerId, kind: kindOf(e), x: e.clientX - rect.left, y: e.clientY - rect.top, t: e.timeStamp,
      button: e.button, buttons: e.buttons, isPrimary: e.isPrimary };
  };
  const layout = () => {
    board?.cancelGesture();
    const rect = canvas.getBoundingClientRect();
    model.setLayout(rect.width, rect.height, safeArea());
  };
  const onDown = (e: PointerEvent) => {
    e.preventDefault();
    canvas.setPointerCapture(e.pointerId);
    const s = sample(e);
    if (board) board.pointerDown(s);
    else model.pointerDown(s);
  };
  const onMove = (e: PointerEvent) => {
    const s = sample(e);
    if (board) board.pointerMove(s);
    else model.pointerMove(s);
  };
  const onUp = (e: PointerEvent) => {
    const s = sample(e);
    if (board) board.pointerUp(s);
    else model.pointerUp(s);
  };
  const onCancel = (e: PointerEvent) => {
    if (board) board.pointerCancel(e.pointerId);
    else model.pointerCancel(e.pointerId);
  };
  const onLostCapture = (e: PointerEvent) => {
    if (e.buttons === 0) board?.pointerUp(sample(e));
    else board?.pointerCancel(e.pointerId);
  };
  const clearDrawings = (e: MouseEvent) => { if (e.button === 0) board?.clearDrawings(); };
  const onKeyDown = (e: KeyboardEvent) => {
    if (!isMovementKey(e.code)) return;
    e.preventDefault();
    model.keyDown(e.code);
  };
  const onKeyUp = (e: KeyboardEvent) => model.keyUp(e.code);
  const onBlur = () => { model.clearKeys(); board?.cancelGesture(); };
  const onVisibility = () => { if (document.hidden) onBlur(); };
  const swallow = (e: Event) => e.preventDefault();

  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointermove', onMove);
  canvas.addEventListener('pointerup', onUp);
  canvas.addEventListener('pointercancel', onCancel);
  canvas.addEventListener('lostpointercapture', onLostCapture);
  canvas.addEventListener('contextmenu', swallow);
  window.addEventListener('pointerdown', clearDrawings, true);
  window.addEventListener('mousedown', clearDrawings, true);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  window.addEventListener('blur', onBlur);
  window.addEventListener('resize', layout);
  layout();

  const detach = () => {
    canvas.removeEventListener('pointerdown', onDown);
    canvas.removeEventListener('pointermove', onMove);
    canvas.removeEventListener('pointerup', onUp);
    canvas.removeEventListener('pointercancel', onCancel);
    canvas.removeEventListener('lostpointercapture', onLostCapture);
    canvas.removeEventListener('contextmenu', swallow);
    window.removeEventListener('pointerdown', clearDrawings, true);
    window.removeEventListener('mousedown', clearDrawings, true);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    window.removeEventListener('blur', onBlur);
    window.removeEventListener('resize', layout);
  };

  /** Fingers already down are forgotten on the way to the board, so no pad stays stuck under it. */
  const setBoard = (target: BoardPointerTarget | null): void => {
    if (target === board) return;
    if (target) model.clearPointers();
    board = target;
  };

  return { model, detach, setBoard };
}
